import { useClassroomContext } from 'contexts/ClassroomContext';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from 'view/components/common/Modal';
import PracticeAssignmentForm from './forms/Practice';
import Classroom from 'models/Classroom';
import {
  faBookOpen,
  faBookOpenReader,
  faCheckToSlot,
  faClipboardList,
  faEarListen,
  faLayerGroup,
  faListUl,
  faMicrophone,
  faPencilAlt,
} from '@fortawesome/free-solid-svg-icons';
import { BookCatalog } from 'view/components/catalog/BookCatalog';
import Assignment from 'models/Assignment';
import CachingEngine from 'utils/CachingEngine';
import Book from 'models/Book';
import EvaluationForm, { EvalScoring } from './forms/Evaluation';
import ItemCard from 'view/components/common/ItemCard';
import Question from 'models/Question';
import Module from 'models/Module';
import useApiCall from 'contexts/ApiCall';
import { moduleService } from 'services/moduleService';

import 'view/style/teacher/components/assignment/assignmentTypeForm.css';

interface AssignmentTypeFormProps {
  module?: Module;
}

const assignmentTypes = [
  {
    displayName: 'Multipart',
    serverName: 'Multi-Part',
    hint: 'Any combination of questions, readings, and other activities in a question by question quiz format',
    icon: faListUl,
    video: require('assets/video/Multipart Example.mp4'),
    color: 'var(--purple)',
  },
  {
    displayName: 'Reading',
    serverName: '',
    hint: 'Students read aloud with immediate missed word feedback in real time',
    icon: faBookOpen,
    video: require('assets/video/Reading Aloud Example.mp4'),
    color: 'var(--dark-green)',
  },
  {
    displayName: 'Listening',
    serverName: 'Read-Aloud',
    hint: 'Like a reading assignment, but the text will be read aloud to the students in a realistic voice',
    icon: faEarListen,
    color: 'var(--dark-blue)',
  },
  {
    displayName: 'Writing',
    serverName: 'Writing',
    hint: 'A freeform writing assignment with automated feedback',
    icon: faPencilAlt,
    color: 'var(--orange)',
  },
];

const evaluationTypes = [
  {
    displayName: 'ESL',
    serverName: 'wida',
    hint: 'Assign your students a short multi-domain test that will give you a reference point for their approximate WIDA level',
    icon: faCheckToSlot,
    color: 'var(--purple)',
  },
  {
    displayName: 'Reading',
    serverName: 'lexile',
    hint: 'Assign your students a short reading test that will give you a reference point for their approximate Lexile level',
    icon: faBookOpenReader,
    color: 'var(--dark-green)',
  },
];

const assessmentTypes = [
  {
    displayName: 'Listening',
    serverName: 'Listening',
    hint: 'Like a Listening assignment, except the student cannot see the words being spoken',
    icon: faEarListen,
    color: 'var(--dark-blue)',
  },
  {
    displayName: 'Speaking',
    serverName: 'Speaking',
    hint: 'Like a Reading assignment, except the feedback is given all at once after the student completes the reading',
    icon: faMicrophone,
    color: 'var(--dark-green)',
  },
];

const LexileScoring: EvalScoring = {
  type: 'lexile',
  min: 0,
  max: 1400,
  default: 400,
  value: 400,
  override: false,
};

const WidaScoring: EvalScoring = {
  type: 'wida',
  min: 0,
  max: 6,
  default: 2,
  value: 2,
  override: false,
};

const AssignmentTypeForm: React.FC<AssignmentTypeFormProps> = ({ module }) => {
  const [showPracticeWindow, setShowPracticeWindow] = useState<boolean>(false);
  const [showEvalWindow, setShowEvalWindow] = useState<boolean>(false);
  const [viewCatalog, setViewCatalog] = useState<boolean>(false);
  const [evalScoring, setEvalScoring] = useState<EvalScoring>(LexileScoring);
  const { classroom } = useClassroomContext();
  const navigate = useNavigate();
  const makeApiCall = useApiCall();

  const navToAssignmentForm = (assignmentType: string) => {
    var navUrl = `/teacher/classroom/${classroom?.id}/new-assignment/${assignmentType}`;
    if (module) {
      navUrl += `/?module=${module.id}`;
    }
    navigate(navUrl);
  };

  const handleSelectCatalogBook = (book: Book, assignmentType?: string) => {
    const newBook = Book.fromBook(book);
    newBook.is_public = false;
    newBook.is_under_review = true;
    CachingEngine.setData('book', newBook);
    const assignment = Assignment.generateDefault(
      classroom as Classroom,
      assignmentType,
    );
    assignment.title = book.title;
    CachingEngine.setData('assignment', assignment);
    setViewCatalog(false);
    navToAssignmentForm(assignmentType ?? 'Default');
  };

  const handleSelectCatalogAssignment = (
    assignment: Assignment,
    book?: Book,
    questions?: Question[],
  ) => {
    CachingEngine.setData('assignment', assignment);
    if (book) CachingEngine.setData('book', book);
    if (questions) CachingEngine.setData('questions', questions);
    setViewCatalog(false);
    navToAssignmentForm(assignment.assignment_type);
  };

  const handleSelectCatalogModule = (module: Module) => {
    if (!classroom) {
      alert(
        'There was an error copying this module. Please refresh the page and try again',
      );
      return;
    }
    makeApiCall(moduleService.copyModule, module.id, classroom?.id).catch(
      (error) => alert(error),
    );
  };

  const evalScorings: Record<string, EvalScoring> = useMemo(
    () => ({
      lexile: LexileScoring,
      wida: WidaScoring,
    }),
    [],
  );

  return (
    <div className="form assignment-type">
      <label htmlFor="assignment-type-form" className="label-large">
        Create an assignment
      </label>
      <div className="form-contents">
        <label htmlFor="assignment-type-form" className="label-normal">
          <strong>Assignments</strong>{' '}
          <i>Best for structured coursework and practice</i>
        </label>
        <div id="assignment-type-form" className="badges-grid">
          {assignmentTypes.map((assignmentType) => (
            <ItemCard
              key={`asgn-type-${assignmentType.displayName}`}
              label={assignmentType.displayName}
              hint={assignmentType.hint}
              icon={assignmentType.icon}
              onClick={() => navToAssignmentForm(assignmentType.serverName)}
              video={assignmentType.video}
              hintTitle={`${assignmentType.displayName} assignment`}
              color={assignmentType.color}
            />
          ))}
          <ItemCard
            key={`asgn-type-practice`}
            label="Flashcards"
            hint="Choose either definition or pronunciation flashcards to target specific words or phrases"
            icon={faLayerGroup}
            onClick={() => setShowPracticeWindow(true)}
            video={require('assets/video/Practice Example.mp4')}
            hintTitle="Flashcards"
            color="var(--gray-9)"
          />
          <ItemCard
            key="asgn-type-from-catalog"
            label="Catalog"
            hint="Use an existing book or assignment from the catalog as a starting point"
            icon={faClipboardList}
            onClick={() => setViewCatalog(true)}
            hintTitle="Import from our catalog"
          />
        </div>
        <label htmlFor="assignment-type-form" className="label-normal">
          <strong>Evaluations</strong>{' '}
          <i>Best for testing and benchmarking your students</i>
        </label>
        <div id="assignment-type-form" className="badges-grid">
          {evaluationTypes.map((evaluationType) => (
            <ItemCard
              key={`asgn-type-${evaluationType.displayName}`}
              label={evaluationType.displayName}
              hint={evaluationType.hint}
              icon={evaluationType.icon}
              onClick={() => {
                setEvalScoring(evalScorings[evaluationType.serverName]);
                setShowEvalWindow(true);
              }}
              hintTitle={`${evaluationType.displayName} evaluation`}
              color={evaluationType.color}
            />
          ))}
          {assessmentTypes.map((assessmentType) => (
            <ItemCard
              key={`asgn-type-${assessmentType.displayName}`}
              label={assessmentType.displayName}
              hint={assessmentType.hint}
              icon={assessmentType.icon}
              onClick={() => navToAssignmentForm(assessmentType.serverName)}
              hintTitle={`${assessmentType.displayName} assessment`}
              color={assessmentType.color}
            />
          ))}
        </div>
      </div>
      <Modal
        isOpen={showPracticeWindow}
        onClose={() => setShowPracticeWindow(false)}
      >
        <PracticeAssignmentForm
          classroom={classroom as Classroom}
          onExit={() => setShowPracticeWindow(false)}
        />
      </Modal>
      <Modal isOpen={showEvalWindow} onClose={() => setShowEvalWindow(false)}>
        <EvaluationForm
          evalScoring={evalScoring}
          onClose={() => setShowEvalWindow(false)}
        />
      </Modal>
      <Modal
        isOpen={viewCatalog}
        onClose={() => setViewCatalog(false)}
        style={{
          width: '100vw',
          height: '90vh',
          maxWidth: '100vw',
          maxHeight: '90vh',
          minWidth: '100vw',
          minHeight: '90vh',
        }}
      >
        <BookCatalog
          onExit={() => setViewCatalog(false)}
          onSelect={handleSelectCatalogBook}
          onSelectAssignment={handleSelectCatalogAssignment}
          onSelectModule={handleSelectCatalogModule}
        />
      </Modal>
    </div>
  );
};

export default AssignmentTypeForm;
